import * as constants from '@/constants';
import i18n from '@/settings/i18n';
import { useFlashMessenger } from './useFlashMessenger';
import { useCallback } from 'react';
import queryString from 'query-string';

import { pages } from '@/settings/pages';
import { FLASH_MESSAGE_LIFETIME_MILLISEC } from '@/constants';
import { FROM_PATH_KEY } from '@/constants';
import { sentryReportError } from '@/utils/sentryReportError';
import { useAppSelector } from '@/store';
import { authStore } from '@/store/auth';

export const useApiErrorHandler = () => {
  const { flashError } = useFlashMessenger();
  // 現在選択中の組織の glopla_id(id_token の glopla_id クレーム由来)。再認証は組織単位で判定するため常に付与する。
  const gloplaId = useAppSelector(authStore.selectors.gloplaIdSelector);

  const redirectToReAuthentication = useCallback(
    (fromPath?: string) => {
      const queryParams = {
        prompt: 'login',
        ...(fromPath && { [FROM_PATH_KEY]: fromPath }),
        ...(gloplaId && { glopla_id: gloplaId }),
      };
      const signinUri = queryString.stringifyUrl({
        url: pages.signin.index,
        query: queryParams,
      });
      location.assign(signinUri);
    },
    [gloplaId],
  );

  const apiErrorHandler = useCallback(
    (err: any, fromPath?: string | undefined) => {
      if (
        err.response &&
        err.response.status === 401 &&
        err.response.data &&
        err.response.data.error === constants.INVALID_ACCESS_TOKEN_ERROR
      ) {
        flashError(i18n.t('ui.error.unexpected_and_login'));
        return;
      }

      if (
        err.response &&
        err.response.data &&
        err.response.data.error === constants.REAUTHENTICATION_EXPIRED_ERROR
      ) {
        flashError(i18n.t('ui.error.reauthentication_expired'));
        setTimeout(() => {
          redirectToReAuthentication(fromPath);
        }, FLASH_MESSAGE_LIFETIME_MILLISEC);
        return;
      }

      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.error === constants.ADMIN_ALREADY_DELETED_ERROR
      ) {
        flashError(i18n.t('ui.error.admin_already_deleted'));
        return;
      }

      // MEMO: 503エラーはインターセプターでメンテナンスメッセージを表示済みのためスキップ
      // src/services/api/axios/interceptors.ts
      if (err.response && err.response.status === 503) {
        return;
      }

      sentryReportError(err);

      flashError(i18n.t('ui.error.unexpected'));
    },
    [flashError, redirectToReAuthentication],
  );

  return { apiErrorHandler };
};
